import {
  useRef,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  RefObject
} from 'react'

import ListItem from '../ListItem'
import * as Styled from './styled'

interface IExpandableOption {
  id: string
}

interface IProps<T extends IExpandableOption> {
  triggerId: string
  triggerIcon: React.FC<React.SVGProps<SVGSVGElement>>
  triggerLabel: string
  options: T[]
  renderOption: (option: T) => ReactNode
  onSelect: (option: T) => void
  scrollableContentWrapperRef: RefObject<HTMLDivElement | null>
  isOptionDisabled?: (option: T) => boolean
}

export const UserDropdownExpandable = <T extends IExpandableOption>({
  triggerId,
  triggerIcon,
  triggerLabel,
  options,
  renderOption,
  onSelect,
  scrollableContentWrapperRef,
  isOptionDisabled
}: IProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<null | HTMLDivElement>(null)

  const toggle = useCallback(() => {
    if (isOpen) {
      scrollableContentWrapperRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
      const checkScroll = setInterval(() => {
        if (scrollableContentWrapperRef.current?.scrollTop === 0) {
          clearInterval(checkScroll)
          setIsOpen(false)
        }
      }, 50)
      return
    }
    setIsOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      wrapperRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [isOpen])

  return (
    <div ref={wrapperRef}>
      <ListItem
        data={{
          id: triggerId,
          icon: triggerIcon,
          label: triggerLabel,
          onClick: toggle,
          isExpandable: true,
          isExpanded: isOpen
        }}
      />
      {isOpen && (
        <div>
          {options.map(option => {
            const disabled = isOptionDisabled?.(option) ?? false
            return (
              <Styled.Row
                key={option.id}
                $disabled={disabled}
                onClick={() => {
                  if (disabled) {
                    return
                  }
                  onSelect(option)
                  toggle()
                }}
              >
                {renderOption(option)}
              </Styled.Row>
            )
          })}
        </div>
      )}
    </div>
  )
}
